import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

export default function LocationAutocomplete({ value, onChange, placeholder = "Ville, pays...", testid = "location-input" }) {
    const [q, setQ] = useState(value || "");
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ left: 0, top: 0, width: 0 });
    const wrapRef = useRef();
    const inputRef = useRef();
    const tref = useRef();

    // Sync prop -> internal q whenever the prop value changes externally
    // (parent selection / clear). Local typing never causes prop updates
    // anymore (consumer only updates on selection), so this is safe.
    useEffect(() => {
        const next = value || "";
        setQ((cur) => (cur === next ? cur : next));
    }, [value]);

    useLayoutEffect(() => {
        if (!open || !wrapRef.current) return;
        const update = () => {
            const el = wrapRef.current;
            if (!el) return; // guard against unmount during scroll/resize
            try {
                const r = el.getBoundingClientRect();
                setPos({ left: r.left, top: r.bottom + 4, width: r.width });
            } catch (err) {
                // Node was detached between check and call — safe to ignore.
                if (process.env.NODE_ENV === "development") {
                    console.debug("LocationAutocomplete: rect skipped", err?.message);
                }
            }
        };
        update();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [open, suggestions]);

    useEffect(() => {
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const fetchSuggestions = (v) => {
        clearTimeout(tref.current);
        if (!v || v.length < 1) { setSuggestions([]); setOpen(false); return; }
        tref.current = setTimeout(async () => {
            try {
                const r = await api.get(`/location/autocomplete?q=${encodeURIComponent(v)}`);
                setSuggestions(r.data || []);
                setOpen((r.data || []).length > 0);
            } catch { setSuggestions([]); setOpen(false); }
        }, 150);
    };

    const handleChange = (v) => {
        setQ(v);
        onChange?.({ raw: v });
        fetchSuggestions(v);
    };

    const select = (s) => {
        const display = `${s.city}, ${s.country}`;
        setQ(display);
        setOpen(false);
        onChange?.({ raw: display, city: s.city, country: s.country, iso2: s.iso2 });
    };

    return (
        <div className="relative" ref={wrapRef}>
            <div className="relative">
                <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input ref={inputRef} value={q}
                    onChange={(e) => handleChange(e.target.value)}
                    onFocus={() => { if (q && q.length >= 1) fetchSuggestions(q); }}
                    placeholder={placeholder}
                    className="pl-9 rounded-full bg-white/90 dark:bg-card text-foreground"
                    data-testid={testid} />
            </div>
            {open && suggestions.length > 0 && createPortal(
                <div
                    style={{ position: "fixed", left: pos.left, top: pos.top, width: pos.width, zIndex: 9999 }}
                    className="bg-popover text-popover-foreground border rounded-2xl shadow-2xl overflow-hidden max-h-72 overflow-y-auto"
                    data-testid="location-suggestions">
                    {suggestions.map((s, i) => (
                        <button key={`${s.city}-${s.country}-${s.iso2}-${i}`} type="button"
                            onMouseDown={(e) => { e.preventDefault(); select(s); }}
                            className="w-full text-left px-4 py-2 hover:bg-accent flex items-center gap-2"
                            data-testid={`location-suggestion-${i}`}>
                            <MapPin size={14} className="text-primary shrink-0" />
                            <span className="font-medium">{s.city}</span>
                            <span className="text-muted-foreground text-xs">{s.country} ({s.iso2})</span>
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
}
